import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectOriginatingTemplateName } from '@/store/slices/project/selectors';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import AddNewSessionModal from './add-new-session/add-new-session-modal';
import SessionCard from './session-card';
import SearchInput from '@ui/search-input/search-input';
import OptionalComponent from '@ui/optional-component/optional-component';
import { SESSION_TYPE_LABELS, SessionTypesEnum } from '@/types/session.types';
import Button from '@ui/buttons/button/button';
import AddNewSessionCard from './add-new-session-card';
import Popover from '@ui/popover/popover';
import { ICONS } from '@ui/icons/types';
import { ESortOptions, filterSessions } from './helpers/filter-sessions';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import SORT_OPTIONS from './helpers/sort-options';
import canAddSessionToProject from '@/utils/can-add-session-to-project';

const SESSION_TYPE_OPTIONS = [
  { label: 'All types', value: '' },
  ...Object.values(SessionTypesEnum).map((val) => ({ label: SESSION_TYPE_LABELS[val], value: val })),
];

type ProjectSessionsRowGridProps = {
  showFilters?: boolean;
};

const ProjectSessionsRowGrid = ({ showFilters }: ProjectSessionsRowGridProps) => {
  const workingProject = useAppSelector(getWorkingProject);
  const user = useAppSelector(getUser);
  const channel = useAppSelector(getActiveChannel);
  const template = useAppSelector(selectOriginatingTemplateName);
  const params = useParams();

  const [showAddNewSessionModal, setShowAddNewSessionModal] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [sessionTypeFilter, setSessionTypeFilter] = useState<{ label: string; value: string } | null>(
    SESSION_TYPE_OPTIONS[0] || null
  );
  const [sortOption, setSortOption] = useState<ESortOptions>(ESortOptions.datedesc);

  const sessions = workingProject?.sessions;

  const projectUuid = params?.projectUuid;
  const language = params?.language;

  const filteredSessions = useMemo(() => {
    return filterSessions({
      language,
      sessionSearch,
      sessionTypeFilter,
      sessions,
      sortOption,
    });
  }, [language, sessionSearch, sessionTypeFilter, sessions, sortOption]);

  const handleCreateSession = async () => {
    if (!projectUuid || !workingProject || workingProject.uuid !== projectUuid) return;
    if (!user || !channel) return;

    setShowAddNewSessionModal(true);
  };

  const handleSessionSearch = useCallback((search: string) => {
    setSessionSearch(search);
  }, []);

  const canAddNewSession = canAddSessionToProject(user);

  return (
    <div data-component="ProjectSessionsRowGrid">
      <Container padding="0 var(--main-content-horizontal-padding)" className="margin-t-24">
        <div className="flex ai-flex-end jc-space-between margin-b-8">
          <Text className="H5">
            Sessions{' '}
            <OptionalComponent display={filteredSessions.length > 0}>
              <Text tag="span" className="H5" color="var(--gray-300)">
                • {filteredSessions.length}
              </Text>
            </OptionalComponent>
          </Text>
          <OptionalComponent display={showFilters}>
            <div className="flex fd-row jc-flex-start ai-center gap-8">
              <Container
                className="flex fd-row flex-1 jc-flex-end"
                margin="0 0 0 auto"
                width="100%"
                background="transparent"
              >
                <SearchInput
                  expandOnFocus
                  width="600px"
                  trackInParams
                  debounceTime={500}
                  isClearable
                  isBorderless
                  onClear={() => {
                    setSessionSearch('');
                  }}
                  onSubmit={handleSessionSearch}
                />
              </Container>

              <Popover
                cycleWithArrowKeys
                width="140px"
                renderTriggerButton={({ open, closing }) => {
                  return (
                    <Button
                      className="S2 flex jc-space-between ai-center padding-v-0 padding-h-12"
                      backgroundColor="transparent"
                      border={open && !closing ? '1px solid var(--gray-200)' : '1px solid var(--gray-600)'}
                      hoverBorder="1px solid var(--gray-200)"
                      width="140px"
                      height="32px"
                    >
                      <Text>{sessionTypeFilter?.label} </Text>
                      <RotatableIcon
                        name={ICONS.KEYBOARD_ARROW_DOWN}
                        size={14}
                        rotate={open && !closing ? '180deg' : '0'}
                      />
                    </Button>
                  );
                }}
                contentClassName="flex-column margin-t-8"
                closeOnClick
                inheritRadius
              >
                <>
                  {Object.values(SESSION_TYPE_OPTIONS).map((option) => {
                    return (
                      <Button
                        key={option.value}
                        className="S2 radius-0 flex"
                        width="100%"
                        disabled={sortOption === option.value}
                        disabledOpacity={1}
                        hoverBackgroundColor={sessionTypeFilter === option ? 'var(--gray-600)' : 'var(--gray-700)'}
                        onClick={() => setSessionTypeFilter(option)}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </>
              </Popover>
              <Popover
                cycleWithArrowKeys
                width="120px"
                renderTriggerButton={({ open, closing }) => {
                  return (
                    <Button
                      className="S2 flex jc-space-between ai-center padding-v-0 padding-h-12"
                      backgroundColor="transparent"
                      border={open && !closing ? '1px solid var(--gray-200)' : '1px solid var(--gray-600)'}
                      hoverBorder="1px solid var(--gray-200)"
                      width="120px"
                      height="32px"
                    >
                      <Text>{SORT_OPTIONS[sortOption].label} </Text>
                      <RotatableIcon
                        name={ICONS.KEYBOARD_ARROW_DOWN}
                        size={14}
                        rotate={open && !closing ? '180deg' : '0'}
                      />
                    </Button>
                  );
                }}
                contentClassName="flex-column margin-t-8"
                closeOnClick
                inheritRadius
              >
                <>
                  {Object.values(SORT_OPTIONS).map(({ label, value }) => {
                    return (
                      <Button
                        key={value}
                        className="S2 radius-0 flex"
                        width="100%"
                        disabled={sortOption === value}
                        disabledOpacity={1}
                        hoverBackgroundColor={sortOption === value ? 'var(--gray-600)' : 'var(--gray-700)'}
                        onClick={() => setSortOption(value)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </>
              </Popover>
            </div>
          </OptionalComponent>
        </div>

        <Container
          width="100%"
          maxWidth="var(--main-content-max-width)" /* prevents cards wrapping when toggling side drawer */
          className="flex jc-flex-start ai-center flex fw-wrap gap-16 radius-8 overflow-hidden"
        >
          <OptionalComponent display={canAddNewSession}>
            <AddNewSessionCard onClick={handleCreateSession} />
          </OptionalComponent>

          {filteredSessions.map((session, index) => {
            return <SessionCard key={session.session} session={session} template={template} index={index} />;
          })}
        </Container>
      </Container>

      <OptionalComponent display={canAddNewSession}>
        <AddNewSessionModal open={showAddNewSessionModal} setOpen={setShowAddNewSessionModal} />
      </OptionalComponent>
    </div>
  );
};

export default ProjectSessionsRowGrid;
